import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { provideTranslocoScope, TranslocoDirective } from '@jsverse/transloco';
import { HeaderCountButtonComponent } from '@rma/public/generic/ui/count-button';
import { ComparePanelComponent } from './ui-compare-panel/compare-panel.component';
import { ComparePanelAgent } from './ui-compare-panel/compare-panel-agent';

const AUTO_DISMISS_MS = 6000;
const HOVER_INTENT_MS = 150;
// Long enough to cross the gap between the button and the panel below it.
const CLOSE_DELAY_MS = 250;

export const SITE_HEADER_SCOPE = 'siteHeader';

@Component({
  selector: 'rma-site-header-compare-agent',
  imports: [ComparePanelComponent, HeaderCountButtonComponent, RouterLink, TranslocoDirective],
  templateUrl: './compare-agent.component.html',
  // Registered here, not left to the parent: an unregistered scope is never fetched.
  providers: [provideTranslocoScope(SITE_HEADER_SCOPE)],
  host: {
    class: 'contents',
    '(document:keydown.escape)': 'dismiss()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SiteHeaderCompareAgentComponent {
  private readonly destroyRef = inject(DestroyRef);

  private readonly anchor = viewChild<ElementRef<HTMLElement>>('anchor');
  private readonly rendered = signal(false);

  protected readonly addedAgentCode = signal<string | null>(null);

  // The host stays mounted when the shortlist empties, so open has to fall with the count.
  protected readonly open = linkedSignal<number, boolean>({
    source: () => this.compareCount(),
    computation: (count, previous) => count > 0 && (previous?.value ?? false),
  });

  // Empty string, not absent: animate.enter reads an empty class list as "no animation".
  protected readonly enterAnimation = computed(() => (this.rendered() ? 'rma-animate-count-button-in' : ''));

  public readonly scope = input.required<string>();
  public readonly compareCount = input.required<number>();
  public readonly agents = input<readonly ComparePanelAgent[]>([]);

  public readonly removeAgent = output<string>();
  public readonly compare = output<void>();

  private autoOpened = false;
  // Hover-open and hover-close are mutually exclusive, so they share one handle.
  private pointerTimer: ReturnType<typeof setTimeout> | undefined;
  private dismissTimer: ReturnType<typeof setTimeout> | undefined;
  private focusTimer: ReturnType<typeof setTimeout> | undefined;

  public constructor() {
    afterNextRender(() => this.rendered.set(true));

    // Latch on the panel appearing, not on the add: a slow or failed agents request would
    // otherwise spend the one opening on a panel nobody saw.
    effect(() => {
      if (this.open() && this.agents().length && !this.autoOpened) {
        this.autoOpened = true;
        this.startDismissTimer();
      }
    });

    this.destroyRef.onDestroy(() => this.clearTimers());
  }

  public showAdded(agentCode: string): void {
    if (this.autoOpened) {
      return;
    }

    this.addedAgentCode.set(agentCode);
    this.open.set(true);
    // A safety close for the case where rows never arrive; restarted once they do.
    this.startDismissTimer();
  }

  // A tap emits pointerenter too, and on touch the button is a link the user is leaving by.
  protected onPointerEnter(event: PointerEvent): void {
    if (event.pointerType !== 'mouse') {
      return;
    }

    clearTimeout(this.pointerTimer);
    clearTimeout(this.dismissTimer);
    this.pointerTimer = setTimeout(() => this.open.set(true), HOVER_INTENT_MS);
  }

  // Delayed: the panel sits below the anchor with a gap the cursor has to cross to reach it.
  protected onPointerLeave(): void {
    clearTimeout(this.pointerTimer);
    this.pointerTimer = setTimeout(() => this.dismiss(), CLOSE_DELAY_MS);
  }

  // :focus-visible, so a tap's leftover focus doesn't open what the tap is navigating away from.
  protected onFocusIn(event: FocusEvent): void {
    if (!(event.target instanceof Element) || !event.target.matches(':focus-visible')) {
      return;
    }

    clearTimeout(this.pointerTimer);
    clearTimeout(this.dismissTimer);
    this.open.set(true);
  }

  // focusout fires before focus lands, so the destination is only knowable a tick later.
  protected onFocusOut(): void {
    clearTimeout(this.focusTimer);
    this.focusTimer = setTimeout(() => {
      if (!this.anchor()?.nativeElement.contains(document.activeElement)) {
        this.dismiss();
      }
    });
  }

  protected onCompare(): void {
    this.dismiss();
    this.compare.emit();
  }

  protected onRemove(agentCode: string): void {
    this.removeAgent.emit(agentCode);
  }

  protected dismiss(): void {
    this.clearTimers();
    this.open.set(false);
    this.addedAgentCode.set(null);
  }

  private startDismissTimer(): void {
    clearTimeout(this.dismissTimer);
    this.dismissTimer = setTimeout(() => this.dismiss(), AUTO_DISMISS_MS);
  }

  private clearTimers(): void {
    clearTimeout(this.pointerTimer);
    clearTimeout(this.dismissTimer);
    clearTimeout(this.focusTimer);
  }
}
